<script setup>
import { useLayout } from '@/layout/composables/layout';
import { requestNotificationPermission, showDesktopNotification } from '@/utils/desktopNotifications';
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import AppSidebar from '@/layout/AppSidebar.vue';
import AppTopbar from '@/layout/AppTopbar.vue';
import CommandPalette from '@/components/CommandPalette.vue';
import UploadPanel from '@/views/pages/documents/drive/UploadPanel.vue';
import AppTabBar from '@/layout/AppTabBar.vue';
import { appStartRoute } from '@/layout/appTabs';
import { ensureNativeCredential, isNativeApp, nativePending, takeNativePending } from '@/utils/nativeApp';
import axios from 'axios';
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { useToast } from 'primevue/usetoast'

const { layoutConfig, layoutState, isSidebarActive } = useLayout();
const store = useStore()
const user = computed(() => store.getters['auth/user'])

function hasRole(roleName) {
  const u = user.value
  if (!u) return false
  if (u.is_super_user) return true
  return u.roles && u.roles.includes(roleName)
}

const outsideClickListener = ref(null);

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const toast = useToast()

// The unread mail count in front of the tab title, "(3) Inbox – Ortic", whatever page sets the title
const mailUnreadCount = computed(() => store.getters['navigation/mailUnreadCount'] || 0)
let titleObserver = null
function applyUnreadTitle() {
    const base = document.title.replace(/^\(\d+\) /, '')
    const wanted = mailUnreadCount.value > 0 && hasRole('MailUser') ? `(${mailUnreadCount.value}) ${base}` : base
    if (document.title !== wanted) document.title = wanted
}
watch(mailUnreadCount, applyUnreadTitle)
onMounted(() => {
    const titleElement = document.querySelector('title')
    if (titleElement) {
        titleObserver = new MutationObserver(applyUnreadTitle)
        titleObserver.observe(titleElement, { childList: true, characterData: true, subtree: true })
    }
})
onBeforeUnmount(() => titleObserver?.disconnect())

const layout = computed(() => {
  return route.meta.layout || 'default'
})
const noPadding = computed(() => !!route.meta.noPadding)

watch(isSidebarActive, (newVal) => {
    if (newVal) {
        bindOutsideClickListener();
    } else {
        unbindOutsideClickListener();
    }
});

// Hide sidebar when route changes
watch(() => route.path, () => {
    layoutState.overlayMenuActive = false;
    layoutState.staticMenuMobileActive = false;
    layoutState.menuHoverActive = false;
});

// Apply theme class to body element for portaled components (datepicker, dropdown, etc.)
watch(user, (newUser) => {
    if (newUser && newUser.theme) {
        // Remove all theme classes from body
        document.body.classList.remove('theme-default', 'theme-condensed', 'theme-condensed-light', 'theme-groovy', 'theme-scifi');
        // Add the current theme class
        document.body.classList.add('theme-' + newUser.theme);
    }
}, { immediate: true });

onMounted(() => {
    // Apply theme class on mount
    if (user.value && user.value.theme) {
        document.body.classList.add('theme-' + user.value.theme);
    }
});

// Global unified SSE stream
let eventSource = null

// Fetch unread counts once user is authenticated
watch(user, (newUser) => {
    if (newUser) {
        if (hasRole('MailUser')) store.dispatch('navigation/fetchMailUnreadCount')
        if (hasRole('ChatUser')) store.dispatch('navigation/fetchChatUnreadCount')
        requestNotificationPermission()
        connectSSE()
    } else {
        disconnectSSE()
    }
}, { immediate: true });

function connectSSE() {
  disconnectSSE()
  const token = store.getters['auth/token']
  if (!token) return

  eventSource = new EventSource(`/api/stream?token=${encodeURIComponent(token)}`)

  eventSource.addEventListener('new-mail', (e) => {
    try {
      const payload = JSON.parse(e.data)
      if (hasRole('MailUser')) store.dispatch('navigation/fetchMailUnreadCount')
      const count = payload.newCount || 1
      // The newest message: its sender and subject; a click opens it
      const title = payload.fromName || t('mail.new_mail')
      const body = count > 1
        ? `${payload.subject || ''}\n${t('mail.new_mail_more', { count: count - 1 })}`
        : (payload.subject || '')
      const target = payload.conversationId
        ? { name: 'mail.conversation', params: { id: payload.conversationId } }
        : { name: 'mail.inbox' }
      showDesktopNotification(title, {
        body,
        // One per conversation: the Android app's background check tags its notifications the same way
        tag: 'mail-' + (payload.conversationId || payload.timestamp),
        channel: 'mail',
        url: router.resolve(target).href,
        onClick: () => router.push(target),
      })
    } catch (err) {
      console.error('Failed to parse SSE mail event:', err)
    }
  })

  // A calendar event starts soon (calendar:remind)
  eventSource.addEventListener('calendar-reminder', (e) => {
    try {
      const payload = JSON.parse(e.data)
      const start = new Date(payload.start_at)
      const when = payload.all_day
        ? start.toLocaleDateString([], { timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long' })
        : start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      showDesktopNotification(payload.title, {
        body: [when, payload.location].filter(Boolean).join(' · '),
        tag: 'calendar-' + (payload.key || payload.id),
        requireInteraction: true,
        channel: 'calendar',
        url: router.resolve({ name: 'calendar.index' }).href,
        onClick: () => router.push({ name: 'calendar.index' }),
      })
      // Also in the page, for when desktop notifications are not allowed
      toast.add({ severity: 'info', summary: payload.title, detail: [when, payload.location].filter(Boolean).join(' · '), life: 30000 })
      window.dispatchEvent(new CustomEvent('calendar-reminder', { detail: payload }))
    } catch (err) {
      console.error('Failed to parse SSE calendar reminder:', err)
    }
  })

  // A queued mail went out or failed: the mail view shows it (MailLayout listens for 'mail-send')
  eventSource.addEventListener('mail-send', (e) => {
    try {
      window.dispatchEvent(new CustomEvent('mail-send', { detail: JSON.parse(e.data) }))
    } catch (err) {
      console.error('Failed to parse SSE mail send event:', err)
    }
  })

  eventSource.addEventListener('new-chat-message', (e) => {
    try {
      const payload = JSON.parse(e.data)
      store.dispatch('navigation/handleChatEvent', payload)
      if (hasRole('ChatUser')) store.dispatch('navigation/fetchChatUnreadCount')
      // Muted by the member's setting for the channel, or the channel is on screen
      if (payload.notify === false || isChannelOnScreen(payload.channelId)) return
      const title = payload.channelType === 'direct'
        ? payload.senderName
        : `${payload.senderName} in #${payload.channelName}`
      showDesktopNotification(title, {
        body: payload.bodyPreview || '',
        tag: 'chat-' + payload.messageId,
        channel: 'chat',
        url: router.resolve({ name: 'chat.channel', params: { channelId: payload.channelId } }).href,
      })
    } catch (err) {
      console.error('Failed to parse SSE chat event:', err)
    }
  })

  eventSource.addEventListener('typing', (e) => {
    try {
      const payload = JSON.parse(e.data)
      store.dispatch('navigation/handleTypingEvent', payload)
    } catch (err) {
      console.error('Failed to parse SSE typing event:', err)
    }
  })

  // Ortic Drive: the Documents page refreshes the folder shown when its space changed elsewhere
  eventSource.addEventListener('drive-changed', (e) => {
    try {
      window.dispatchEvent(new CustomEvent('drive-changed', { detail: JSON.parse(e.data) }))
    } catch (err) {
      console.error('Failed to parse SSE drive event:', err)
    }
  })

  eventSource.onerror = () => {
    console.warn('SSE connection error, will auto-reconnect...')
  }
}

function isChannelOnScreen(channelId) {
  return document.visibilityState === 'visible' && document.hasFocus()
    && route.name === 'chat.channel' && Number(route.params.channelId) === channelId
}

function disconnectSSE() {
  if (eventSource) {
    eventSource.close()
    eventSource = null
  }
}

onBeforeUnmount(() => {
  disconnectSSE()
});

// The Android app: a tapped notification opens its page; a share, a mailto: link or an .ics file goes to the view
// that takes it (MailLayout, CalendarLayout), once the user is signed in
function onNativeOpen(event) {
  const url = event.detail?.url
  // "/" is where the app starts: its first tab (appTabs.js), not the home page
  if (url === '/') router.push(appStartRoute(user.value))
  else if (url) router.push(url)
}
function routeNativePending() {
  if (!user.value) return
  const pending = takeNativePending()
  if (!pending) return
  nativePending.value = pending
  router.push({ name: pending.action === 'import-ics' ? 'calendar.index' : 'mail.inbox' })
}
// The back button closes an open dialog first, on whatever page; a capture listener runs before the views' own
// (MailLayout, CalendarLayout …), whose dialogs close the same way
function closeNativeDialog(event) {
  const masks = document.querySelectorAll('.p-dialog-mask, .p-drawer-mask')
  if (!masks.length) return
  event.preventDefault()
  event.stopImmediatePropagation()
  const close = masks[masks.length - 1].querySelector('.p-dialog-close-button, .p-drawer-close-button')
  if (close) close.click()
  else document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape', bubbles: true }))
}
if (isNativeApp) {
  onMounted(() => {
    window.addEventListener('ortic-app-open', onNativeOpen)
    window.addEventListener('ortic-app-pending', routeNativePending)
    window.addEventListener('ortic-app-back', closeNativeDialog, true)
  })
  onBeforeUnmount(() => {
    window.removeEventListener('ortic-app-open', onNativeOpen)
    window.removeEventListener('ortic-app-pending', routeNativePending)
    window.removeEventListener('ortic-app-back', closeNativeDialog, true)
  })
  watch(user, routeNativePending, { immediate: true })
  watch(user, (u) => { if (u) ensureNativeCredential(axios, u).catch((e) => console.warn('No background check:', e)) }, { immediate: true })
}

const containerClass = computed(() => {
    let classes = {
        'layout-native-app': isNativeApp,
        'layout-overlay': layoutConfig.menuMode === 'overlay',
        'layout-static': layoutConfig.menuMode === 'static',
        'layout-static-inactive': layoutState.staticMenuDesktopInactive && layoutConfig.menuMode === 'static',
        'layout-overlay-active': layoutState.overlayMenuActive,
        'layout-mobile-active': layoutState.staticMenuMobileActive
    };
    if (user.value) {
        classes['theme-' + user.value.theme] = true
    }
    return classes;
});

function bindOutsideClickListener() {
    if (!outsideClickListener.value) {
        outsideClickListener.value = (event) => {
            if (isOutsideClicked(event)) {
                layoutState.overlayMenuActive = false;
                layoutState.staticMenuMobileActive = false;
                layoutState.menuHoverActive = false;
            }
        };
        document.addEventListener('click', outsideClickListener.value);
    }
}

function unbindOutsideClickListener() {
    if (outsideClickListener.value) {
        document.removeEventListener('click', outsideClickListener);
        outsideClickListener.value = null;
    }
}

function isOutsideClicked(event) {
    const sidebarEl = document.querySelector('.layout-sidebar');
    const topbarEl = document.querySelector('.layout-menu-button');

    return !(sidebarEl.isSameNode(event.target) || sidebarEl.contains(event.target) || topbarEl.isSameNode(event.target) || topbarEl.contains(event.target));
}
</script>

<template>
    <div class="layout-wrapper" :class="containerClass">
        <template v-if="layout === 'app'">
            <!-- The Android app has its own navigation at the bottom -->
            <template v-if="!isNativeApp">
                <app-topbar></app-topbar>
                <app-sidebar></app-sidebar>
            </template>
            <div class="layout-main-container">
                <div class="layout-main" :class="{ 'no-padding': noPadding }">
                    <router-view :key="$route.meta.viewKey || $route.fullPath"></router-view>
                </div>
            </div>
            <app-tab-bar v-if="isNativeApp"></app-tab-bar>
            <div v-if="!isNativeApp" class="layout-mask animate-fadein"></div>
            <command-palette></command-palette>
            <upload-panel></upload-panel>
        </template>

        <template v-if="layout === 'guest'">
            <router-view :key="$route.fullPath"></router-view>
        </template>
        
    </div>
    <Toast />
</template>

<style>
/* The Android app: no topbar or sidebar, the page fills what the tab bar leaves (views size themselves with --app-height) */
.layout-native-app {
    --app-tabbar-height: 3.5rem;
    --app-height: calc(100dvh - var(--app-tabbar-height));
}

.app-typing .layout-native-app {
    --app-tabbar-height: 0px;
}

.layout-wrapper.layout-native-app .layout-main-container {
    margin-left: 0 !important;
    padding: 0 !important;
    min-height: 0;
    height: var(--app-height) !important;
    overflow-y: auto;
}

.layout-wrapper.layout-native-app .layout-main {
    padding: 1rem;
}

/* Room to scroll the page's end out from under a form's buttons (OrticActionToolbar) */
body.has-action-toolbar .layout-wrapper.layout-native-app .layout-main {
    padding-bottom: 4.5rem;
}
</style>
